import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiMail, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const LoginContainer = styled.div`
  min-height: calc(100vh - 160px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 2rem;
  width: 100%;
  max-width: 680px;
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const LoginTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: var(--gray-800);
  margin-bottom: 0.5rem;
`;

const LoginSubtitle = styled.p`
  color: var(--gray-600);
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-weight: 500;
  color: var(--gray-700);
`;

const InputContainer = styled.div`
  position: relative;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: ${props => props.hasIcon ? '3rem' : '1rem'};
  border: 2px solid var(--gray-200);
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    color: var(--gray-600);
  }
`;

  const LoginButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover:not(:disabled) {
    background: var(--primary-dark);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  `;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--gray-200);
  }
  
  span {
    padding: 0 1rem;
    color: var(--gray-500);
    font-size: 0.875rem;
  }
`;

  const RegisterLink = styled(Link)`
  text-align: center;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: var(--transition);
  
  &:hover {
    text-decoration: underline;
  }
  `;

const AuxLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.75rem;
  font-size: 0.95rem;

  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;
  }

  a:hover { text-decoration: underline; }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: var(--border-radius);
  font-size: 0.875rem;
`;

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const { login, loading, user, hasRole, refreshRoles } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      // Réinitialiser le formulaire et retarder la redirection pour une meilleure UX
      setFormData({ email: '', password: '' });
      setShowPassword(false);

      // Rafraîchir les rôles depuis l'API pour décider de la destination
      try {
        await refreshRoles?.();
      } catch (_) {}
      let currentRoles = [];
      try { currentRoles = JSON.parse(localStorage.getItem('roles') || '[]'); } catch (_) {}
      const userRoleMeta = user?.user_metadata?.role;
      const isAdmin = (hasRole?.('admin')) || currentRoles.includes('admin') || userRoleMeta === 'admin';
      const destination = isAdmin ? '/admin' : '/dashboard';
      navigate(destination, { replace: true });
    } else {
      let message = result.message || 'Erreur de connexion';
      // Mapping des erreurs Supabase en messages utilisateurs
      if (/email.*not.*confirmed/i.test(message) || /non vérifié/i.test(message)) {
        message = 'Email non vérifié. Veuillez valider votre adresse avant de vous connecter.';
      } else if (/invalid login credentials/i.test(message)) {
        message = 'Identifiants invalides. Vérifiez votre email et mot de passe.';
      }
      setError(message);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <LoginTitle>Connexion</LoginTitle>
          <LoginSubtitle>Se connecter à Lokali</LoginSubtitle>
        </LoginHeader>

        <LoginForm onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormGroup>
            <FormLabel>Email</FormLabel>
            <InputContainer>
              <FormInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                required
              />
              <InputIcon>
                <FiMail />
              </InputIcon>
            </InputContainer>
          </FormGroup>

          <FormGroup>
            <FormLabel>Mot de passe</FormLabel>
            <InputContainer>
              <FormInput
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Votre mot de passe"
                required
              />
              <InputIcon onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </InputIcon>
            </InputContainer>
          </FormGroup>

          <LoginButton type="submit" disabled={loading}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </LoginButton>

          <Divider>
            <span>ou</span>
          </Divider>

          <RegisterLink to="/register">
            Créer un compte
          </RegisterLink>

          <AuxLinks>
            <Link to="/forgot-password">Mot de passe oublié</Link>
          </AuxLinks>
        </LoginForm>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
